import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowsClockwise, MagnifyingGlass, Plus, UploadSimple, WarningCircle, X} from '../../../icons';
import {Spinner} from '../../../Spinner';
import {YesNoModal} from '../../../elements/YesNoModal';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {DocumentRawModal} from '../../memory/DocumentRawModal';
import {DocumentKindModal} from '../../memory/DocumentKindModal';
import {DocumentDeleteModal} from '../../memory/DocumentDeleteModal';
import {deletedMessage, deleteErrorMessage} from '../../memory/deleteMessages';
import {DriveConnectModal} from '../../memory/DriveConnectModal';
import {DOC_STATUS, REFRESH_CONFIRM_REASONS, URL_ERROR_KEYS} from '../../memory/docConstants';
import {INGEST_ACCEPT} from '../../memory/ingestFiles';
import {UPLOAD_STATUS, useMemoryIngest} from '../useMemoryIngest';
import {SourceRow} from './SourceRow';
import {
    LIB_ALL, LIB_KIND_ORDER, LIB_LIST_CAP, LIB_PERIOD, connectionAwaitsFirstRead, driveKeyOf,
    libEntries, libKindLabelKey, libMatches, libPeriodRange,
} from './sourcesModel';

/**
 * La biblioteca «Documentos y notas»: UNA lista con todo el material que alimenta la memoria, con
 * filtros por período y por tipo.
 *
 * Reemplaza a `MemorySourcesView` DENTRO del Recorrido (el 08/09 fede y Pablo eligieron el modal
 * centrado del mockup sobre el panel lateral del 03/09). La vista vieja sigue viva e intacta en la
 * Memoria clásica: no es un rediseño de aquélla, es otra vista — lo que cambió no es la piel sino
 * la forma de la información.
 *
 * Las tres diferencias que la hacen otra vista, y no `MemorySourcesView` con props:
 *
 *  1. **Una sola lista.** La vieja tiene dos bloques con título propio («Archivos de Google Drive»
 *     arriba, «Reuniones y documentos cargados» abajo) y buscar algo obligaba a saber de antemano
 *     por dónde había entrado. Acá los archivos de Drive van mezclados por fecha con el resto, con
 *     su ícono de origen y su «Actualizar» en la misma fila. Drive se relee SOLO todos los días a
 *     las 5 (cron `trigger_source_connections_sync` del backend) y la fila lo dice («Se actualiza
 *     todos los días · última: …»); el botón está a la vista para traer los cambios sin esperar.
 *     Hasta el 01/10 este comentario decía que la sincronización era a mano: era falso.
 *  2. **Filas, no tabla.** Ver `SourceRow`.
 *  3. **Sin el strip «Contexto del cliente 1/6»** (`MemoryContextPack`). Pablo: «es otra vez darle
 *     la sensación de que eso tiene que estar sí o sí». En la Memoria clásica el strip se queda.
 *
 * ⚠️ **El conteo de hechos sale de `facts_extracted_count` y de ningún otro lado.** La tabla
 * clásica lo calcula pidiendo `listFacts` y agrupando por `source_document_id`; acá no se puede
 * copiar eso: el backend manda `facts_extracted_count` SÓLO si la agencia tiene la Memoria en nivel
 * TOTAL, y contar los hechos por nuestra cuenta le mostraría a una agencia en nivel MEDIO el
 * tamaño de una capa que no tiene (y a una degradada de TOTAL a MEDIO, sus hechos viejos).
 *
 * **Tres estados, no dos** (QA 2026-09-09, QA-docs-modal-00): «cargando», «no pude leer» y «no hay
 * nada». El camino de error pintaba el estado vacío de «cargá algo», así que un 404 por cliente sin
 * acceso se leía como una memoria virgen y mandaba a subir de nuevo material que ya estaba.
 */

// Cuánto esperar entre vueltas mientras haya algo entrando: un documento extrayéndose (con
// `pollDocuments`) o un archivo de Drive recién conectado esperando su primera lectura (siempre).
const POLL_MS = 8000;
// Vueltas de fondo SEGUIDAS que pueden fallar antes de dejar de mirar. Una respuesta mala no corta
// el seguimiento (mismo criterio que `FeedActContent`).
const MAX_POLL_RETRIES = 3;

const inFlight = (doc) => doc.status === DOC_STATUS.PENDING || doc.status === DOC_STATUS.EXTRACTING;

export function SourcesLibrary({
    clientId,
    clientName,
    refreshToken = 0,
    openConnectToken = 0,
    // Avisa hacia arriba que entró material por ESTE modal. Sin esto, lo que se carga acá no
    // aparece en la lista de ingesta ni en el narrador, y el mazo de Revisa no se entera de los
    // hechos nuevos hasta un F5: los dos viven en la página de atrás y se enteran por el
    // contenedor, no por el modal.
    onIngested,
    // Los hechos del cliente cambiaron por un borrado con cascada. NO alcanza `onIngested`: aquél
    // refresca listados pero no el store de hechos, y el mazo de Revisa se quedaba con cartas de un
    // documento ya borrado, aprobables y todo (QA-09 / review C-borrado-04).
    onFactsChanged,
    // El Recorrido lo apaga: ahí el acto Alimenta ya pollea este mismo endpoint y avisa por
    // `refreshToken` cuando una extracción termina (`handleDocumentReady`). Dos pollers contra el
    // mismo endpoint en la misma pantalla es pagar dos veces por el dato — es el «doble poll» que
    // sacar la tabla del acto vino a resolver el 03/09. La excepción es un archivo de Drive recién
    // conectado: hasta que su documento aparece no hay nada que el acto pueda seguir, así que eso se
    // mira acá igual (`followDriveArrivals`).
    pollDocuments = true,
}) {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [documents, setDocuments] = useState([]);
    const [connections, setConnections] = useState([]);
    const [driveConfig, setDriveConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    // Filtros. Arrancan en «Todo» a propósito: la lista se abre para buscar algo, y un filtro
    // puesto de fábrica esconde material sin que nadie lo haya pedido.
    const [kind, setKind] = useState(LIB_ALL);
    // El buscador por nombre (Pablo lo esperaba el 10/09 y no estaba). Sin debounce a propósito:
    // el listado tiene como mucho `LIB_LIST_CAP` filas y el filtro es un `includes` sobre el
    // nombre — re-filtrar por tecla es más barato que el retraso que se sentiría esperando.
    const [query, setQuery] = useState('');
    const [preset, setPreset] = useState(LIB_PERIOD.ALL);
    const [range, setRange] = useState({from: '', to: ''});

    const [busyId, setBusyId] = useState(null);
    const [syncingId, setSyncingId] = useState(null);
    const [media, setMedia] = useState({});

    const [rawTarget, setRawTarget] = useState(null);
    const [kindTarget, setKindTarget] = useState(null);
    const [savingKind, setSavingKind] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [reprocessTarget, setReprocessTarget] = useState(null);
    const [confirmTarget, setConfirmTarget] = useState(null);
    const [connectOpen, setConnectOpen] = useState(false);

    const pollRef = useRef(null);
    const fileRef = useRef(null);

    const clearPoll = useCallback(() => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    // El fetch vive en un ref para que el poll pueda llamarse a sí mismo sin que el efecto de
    // montaje dependa de una función que cambia en cada render (y vuelva a pedir todo).
    const fetchRef = useRef(null);

    // El modal se desmonta al cerrarse (`unmountOnClose`): una respuesta que llega después no puede
    // tocar el estado ni volver a agendar, o queda un sondeo huérfano de la instancia muerta.
    const aliveRef = useRef(true);
    // Cada pedido lleva su número y sólo el último manda: una vuelta vieja (del cliente anterior, o
    // una que se cruzó con «Actualizar») no pisa a la nueva.
    const seqRef = useRef(0);
    // Lo último que se pintó de ESTE cliente. Con algo en pantalla, una vuelta de fondo que falla
    // calla y lo conserva (`custom-components.md`); sin nada, es un error de carga.
    const hasDataRef = useRef(false);
    const connsRef = useRef([]);
    const pollingRef = useRef(false);
    const failuresRef = useRef(0);
    // Conexiones de Drive que esta visita vio nacer o esperando su primera lectura, y los documentos
    // suyos que ya se avisaron hacia arriba.
    const followedRef = useRef(new Set());
    const announcedRef = useRef(new Set());
    // Por ref: el padre lo recrea en cada render y como dependencia rearmaría la cadena del fetch.
    const onIngestedRef = useRef(onIngested);
    onIngestedRef.current = onIngested;

    const schedulePoll = useCallback(() => {
        clearPoll();
        pollRef.current = setTimeout(() => fetchRef.current(true), POLL_MS);
    }, [clearPoll]);

    /** Una vuelta fallida: sin nada en pantalla es un error de carga; con algo, se reintenta callado. */
    const handleBadRound = useCallback((silent) => {
        if (!hasDataRef.current) {
            // No se pinta como «no hay nada»: son dos situaciones distintas y mandan a lugares
            // opuestos. El toast se va solo; el estado queda.
            setLoadError(true);
            if (!silent) toast.error(t('client_memory.toast.load_error'));
            return;
        }
        if (!pollingRef.current) return;
        failuresRef.current += 1;
        if (failuresRef.current <= MAX_POLL_RETRIES) schedulePoll();
    }, [schedulePoll, t]);

    /**
     * Un archivo de Drive recién conectado: el sync crea su documento y marca la conexión leída en el
     * MISMO paso (`sync_source_connection._sync`), así que «ya tiene `last_synced_at`» no quiere decir
     * «ya está leído» — el documento nace en cola. Al aparecer se avisa UNA vez hacia arriba
     * (`onIngested`): en el Recorrido es el acto Alimenta el que sigue la extracción (narra, avisa a
     * Revisa y, al terminar, refresca esta lista por `refreshToken`). Sin quien lo siga, se lo sigue
     * acá. Devuelve si hay que seguir mirando por ese documento.
     */
    const followDriveArrivals = useCallback((docs, conns) => {
        const known = new Set(connsRef.current.map((conn) => conn.id));
        conns.forEach((conn) => {
            // Nueva en esta visita (después de la primera carga) o esperando su primera lectura.
            const isNew = hasDataRef.current && !known.has(conn.id);
            if (isNew || connectionAwaitsFirstRead(conn)) followedRef.current.add(conn.id);
        });
        const keys = new Set(conns.filter((conn) => followedRef.current.has(conn.id))
            .map((conn) => conn.external_id));
        const arrived = docs.filter((doc) => keys.has(driveKeyOf(doc)));
        arrived.forEach((doc) => {
            if (announcedRef.current.has(doc.id)) return;
            announcedRef.current.add(doc.id);
            if (onIngestedRef.current) onIngestedRef.current(doc);
        });
        return !onIngestedRef.current && arrived.some(inFlight);
    }, []);

    const fetchAll = useCallback((silent = false) => {
        const seq = seqRef.current + 1;
        seqRef.current = seq;
        // Este pedido decide si se vuelve a mirar: un timer viejo no puede abrir una segunda cadena.
        clearPoll();
        if (!silent) setLoading(true);
        return Promise.all([
            ClientMemoryApi.listDocuments(clientId),
            ClientMemoryApi.listConnections(clientId),
        ]).then(([docsResp, connResp]) => {
            if (!aliveRef.current || seq !== seqRef.current) return;
            setLoading(false);
            const docs = docsResp && Array.isArray(docsResp.documents) ? docsResp.documents : null;
            if (!docs) {
                handleBadRound(silent);
                return;
            }
            // Sin el listado de conexiones se conservan las de la vuelta anterior: con `[]` las
            // filas de Drive perdían su «Actualizar» y su estado, y el sondeo se apagaba.
            const connsOk = !!connResp && Array.isArray(connResp.connections);
            const conns = connsOk ? connResp.connections : connsRef.current;
            const followingDoc = followDriveArrivals(docs, conns);
            hasDataRef.current = true;
            connsRef.current = conns;
            setLoadError(false);
            setDocuments(docs);
            setConnections(conns);
            // Sólo se vuelve a mirar si hay algo entrando: sin material en vuelo, la lista es
            // estable y otro poll es pagar por el mismo dato. Un archivo de Drive recién conectado
            // también es algo entrando, y ése se mira aunque el Recorrido apague `pollDocuments`: el
            // acto Alimenta pollea documentos, no conexiones (card pH4QPmhG).
            pollingRef.current = (pollDocuments && docs.some(inFlight))
                || conns.some((conn) => connectionAwaitsFirstRead(conn))
                || followingDoc;
            if (!connsOk) {
                handleBadRound(true);
                return;
            }
            failuresRef.current = 0;
            if (pollingRef.current) schedulePoll();
        }).catch(() => {
            if (!aliveRef.current || seq !== seqRef.current) return;
            setLoading(false);
            handleBadRound(silent);
        });
    }, [clientId, clearPoll, followDriveArrivals, handleBadRound, pollDocuments, schedulePoll]);

    fetchRef.current = fetchAll;

    useEffect(() => {
        ClientMemoryApi.getDriveConfig()
            .then((cfg) => setDriveConfig(cfg || {configured: false}))
            .catch(() => setDriveConfig({configured: false}));
    }, []);

    // El montaje y cada ingesta de arriba (`refreshToken`) traen la lista de nuevo. En silencio
    // salvo la primera vez: la lista ya está en pantalla y un spinner haría parpadear todo lo que
    // el usuario está mirando.
    //
    // Dispara por `refreshToken` y `clientId` y NADA más — el fetch entra por el ref a propósito.
    // Con `fetchAll` en las dependencias, cualquier cosa que le cambie la identidad (hoy `t`, que
    // se rehace cuando i18next emite) se convierte en una vuelta más contra el mismo endpoint.
    const seenToken = useRef(null);
    useEffect(() => {
        const first = seenToken.current === null;
        seenToken.current = refreshToken;
        fetchRef.current(!first);
    }, [refreshToken, clientId]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Cambió el cliente: lo que está en pantalla es del anterior y hay que soltarlo ANTES de que
    // llegue la respuesta nueva. Sin esto la lista sigue mostrando el material del cliente que se
    // acaba de dejar, y una acción de fila viajaría con el `clientId` nuevo sobre un documento
    // ajeno. Lo mismo hace `FeedActContent` con su listado.
    const seenClient = useRef(clientId);
    useEffect(() => {
        if (seenClient.current === clientId) return;
        seenClient.current = clientId;
        clearPoll();
        hasDataRef.current = false;
        connsRef.current = [];
        pollingRef.current = false;
        failuresRef.current = 0;
        followedRef.current = new Set();
        announcedRef.current = new Set();
        setDocuments([]);
        setConnections([]);
        setMedia({});
        setBusyId(null);
        setSyncingId(null);
        setLoadError(false);
        setLoading(true);
    }, [clientId, clearPoll]);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            clearPoll();
        };
    }, [clearPoll]);

    const driveReady = !!(driveConfig && driveConfig.configured);

    // `?connect=drive` (el atajo del menú «+ Agregar») abre el Picker al aterrizar. Lo consumía
    // `MemoryConnectionsView`, que ya no vive acá adentro; el flag se BORRA de la URL para que un
    // segundo click del menú lo vuelva a disparar.
    useEffect(() => {
        if (searchParams.get('connect') !== 'drive') return;
        if (!driveConfig) return;
        if (driveConfig.configured) setConnectOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete('connect');
        setSearchParams(next, {replace: true});
    }, [searchParams, driveConfig, setSearchParams]);

    // La segunda puerta al Picker: el botón de Drive de la Barra Total, que abre este modal y pide
    // el selector sin navegar (o sea, sin query que leer). El primer valor no abre nada — sería
    // abrir el Picker por el solo hecho de montar la vista.
    const seenConnectToken = useRef(openConnectToken);
    useEffect(() => {
        if (openConnectToken === seenConnectToken.current) return;
        seenConnectToken.current = openConnectToken;
        if (driveReady) setConnectOpen(true);
    }, [openConnectToken, driveReady]);

    // --- cargar desde el modal --------------------------------------------------------------
    // Instancia propia de la ingesta: el que llega hasta acá y se acuerda de que le falta algo
    // («lo quería cargar acá») no tiene por qué cerrar y volver a buscar la barra. Una segunda
    // instancia no cuesta nada — su único efecto es un `beforeunload` que sólo hace algo con
    // subidas binarias en vuelo.
    const handleIngested = useCallback((doc) => {
        fetchRef.current(true);
        if (onIngested) onIngested(doc);
    }, [onIngested]);
    const {ingestFiles, uploads = [], cancelUpload, dismissUpload} = useMemoryIngest(
        clientId, handleIngested);
    const uploading = uploads.some((u) => u && u.status === UPLOAD_STATUS.UPLOADING);
    const failedUploads = uploads.filter((u) => u && u.status === UPLOAD_STATUS.FAILED);

    const pickFiles = (event) => {
        const files = event.target.files;
        if (files && files.length) ingestFiles(files);
        // El input se limpia para que elegir DOS VECES el mismo archivo dispare las dos veces:
        // sin esto el `change` no vuelve a salir y el segundo intento se lee como que no pasó nada.
        event.target.value = '';
    };

    // --- acciones de fila -------------------------------------------------------------------

    const replaceDoc = (updated) => setDocuments(
        (prev) => prev.map((d) => (d.id === updated.id ? updated : d)));

    const listen = (doc) => {
        setMedia((current) => ({...current, [doc.id]: {loading: true}}));
        ClientMemoryApi.getDocumentMediaUrl(doc.id, clientId)
            .then((data) => setMedia((current) => ({
                ...current, [doc.id]: data && data.url ? {url: data.url} : {error: true},
            })))
            .catch(() => setMedia((current) => ({...current, [doc.id]: {error: true}})));
    };

    const retry = (doc) => {
        setBusyId(doc.id);
        ClientMemoryApi.retryDocument(doc.id, clientId)
            .then((data) => {
                setBusyId(null);
                if (data && data.status === 'OK') {
                    replaceDoc(data.data);
                    // `queued`: la tarea sigue en la cola y no se reintentó nada.
                    if (data.queued) toast.info(t('client_memory.documents.retry_still_queued'));
                    else toast.success(t('client_memory.documents.retry_ok'));
                    fetchRef.current(true);
                } else toast.error(t('client_memory.toast.error'));
            })
            .catch(() => {
                setBusyId(null);
                toast.error(t('client_memory.toast.error'));
            });
    };

    const reprocess = () => {
        const doc = reprocessTarget;
        if (!doc) return;
        setReprocessTarget(null);
        setBusyId(doc.id);
        ClientMemoryApi.reprocessDocument(doc.id, clientId)
            .then((data) => {
                setBusyId(null);
                if (data && data.status === 'OK') {
                    replaceDoc(data.data);
                    toast.success(t('client_memory.documents.reprocess_ok'));
                    fetchRef.current(true);
                } else toast.error(t('client_memory.toast.error'));
            })
            .catch(() => {
                setBusyId(null);
                toast.error(t('client_memory.toast.error'));
            });
    };

    // Volver a leer la página de un documento cargado desde un enlace. Los dos rechazos del guard
    // (la página trajo mucho menos texto, la página lleva a otra dirección) NO son errores: se
    // preguntan con el dato del rechazo, porque sólo el usuario sabe si su página cambió de verdad.
    const refreshUrl = (doc, force = false) => {
        setConfirmTarget(null);
        setBusyId(doc.id);
        ClientMemoryApi.refreshUrlDocument(doc.id, clientId, force)
            .then((data) => {
                setBusyId(null);
                if (data && data.status === 'OK') {
                    replaceDoc(data.data);
                    toast.success(t('client_memory.documents.refresh_url_ok'));
                    fetchRef.current(true);
                    return;
                }
                if (data && data.status === 'UNCHANGED') {
                    toast.info(t('client_memory.documents.refresh_url_unchanged'));
                    return;
                }
                const reason = data && data.reason_code;
                if (REFRESH_CONFIRM_REASONS[reason]) {
                    setConfirmTarget({doc, reason, body: (data && data.raw_body) || {}});
                    return;
                }
                toast.error(URL_ERROR_KEYS[reason]
                    ? t(URL_ERROR_KEYS[reason]) : t('client_memory.toast.error'));
            })
            .catch(() => {
                setBusyId(null);
                toast.error(t('client_memory.toast.error'));
            });
    };

    const saveKind = (nextKind) => {
        const doc = kindTarget;
        if (!doc) return;
        setSavingKind(true);
        ClientMemoryApi.patchDocumentKind(doc.id, clientId, nextKind)
            .then((data) => {
                setSavingKind(false);
                if (data && data.status === 'OK') {
                    replaceDoc(data.data);
                    setKindTarget(null);
                    toast.success(t('client_memory.documents.kind_ok'));
                } else toast.error(t('client_memory.toast.error'));
            })
            .catch(() => {
                setSavingKind(false);
                toast.error(t('client_memory.toast.error'));
            });
    };

    // `withExtracted` lo decide el diálogo: borrar sólo el material, o también los hechos, el
    // resumen y los eventos que salieron de él (card VLtk3vc7).
    const remove = (withExtracted) => {
        const doc = deleteTarget;
        if (!doc) return;
        setDeleting(true);
        ClientMemoryApi.deleteDocument(doc.id, clientId, withExtracted)
            .then((data) => {
                setDeleting(false);
                setDeleteTarget(null);
                if (data && data.status === 'OK') {
                    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
                    toast.success(deletedMessage(t, withExtracted, data.data));
                    // El mazo de Revisa cuenta los hechos que este borrado acaba de sacar, y vive
                    // en la página de atrás: sin el aviso sigue mostrando —y dejando curar— una
                    // cola que ya no existe, hasta un F5.
                    if (withExtracted && onFactsChanged) onFactsChanged();
                } else toast.error(deleteErrorMessage(t, data));
            })
            .catch(() => {
                setDeleting(false);
                setDeleteTarget(null);
                toast.error(t('client_memory.toast.error'));
            });
    };

    const syncConnection = (connection) => {
        setSyncingId(connection.id);
        ClientMemoryApi.syncConnection(connection.id, clientId)
            .then((data) => {
                if (data && data.status === 'OK') {
                    toast.success(t('client_memory.connections.toast.synced'));
                } else toast.error(t('client_memory.connections.toast.error'));
                return fetchRef.current(true);
            })
            .catch(() => toast.error(t('client_memory.connections.toast.error')))
            .finally(() => setSyncingId(null));
    };

    // --- filtros ----------------------------------------------------------------------------

    const applyPreset = (value) => {
        setPreset(value);
        setRange(libPeriodRange(value));
    };

    // Tocar una fecha a mano apaga el preset: el rango dejó de ser «los últimos 30 días».
    const setBound = (which, value) => {
        setPreset(null);
        setRange((prev) => ({...prev, [which]: value}));
    };

    const entries = useMemo(() => libEntries(documents, connections), [documents, connections]);
    const visible = useMemo(
        () => entries.filter((entry) => (entry.document
            ? libMatches(entry.document, {kind, from: range.from, to: range.to, query})
            // Una conexión sin documento no tiene tipo todavía: cae en «Documentos», que es lo que
            // va a ser cuando el sync la traiga. Su nombre es el rótulo del archivo en Drive.
            : libMatches(
                {source: 'DRIVE', created_on: entry.date, title: entry.connection.external_label},
                {kind, from: range.from, to: range.to, query}))),
        [entries, kind, range.from, range.to, query]);

    const filtering = kind !== LIB_ALL || !!range.from || !!range.to || !!query.trim();

    // Suelta los TRES filtros de una, que es lo que el usuario quiere decir con «limpiar»: dejar
    // uno puesto devuelve una lista que sigue sin explicarse.
    const clearFilters = () => {
        setKind(LIB_ALL);
        setQuery('');
        setPreset(LIB_PERIOD.ALL);
        setRange({from: '', to: ''});
    };
    // El backend corta el listado en `LIST_CAP`, y acá se filtra sobre lo que llegó: con la lista
    // completa el filtro de período diría «ningún material coincide» sobre material que existe.
    // Decirlo es lo mínimo; el filtro server-side es otra card.
    const atCap = documents.length >= LIB_LIST_CAP;

    const client = clientName || t('ia_booster.this_client');

    // El título se dibuja SIEMPRE, también mientras carga: el modal se anuncia con
    // `aria-labelledby="iab-sheet-title"`, y con el encabezado detrás del `loading` ese id no
    // existía en el primer segundo — un diálogo sin nombre accesible.
    const head = (
        <header className="iab-src-head">
            <h2 className="iab-src-title" id="iab-sheet-title">
                {t('ia_booster.sources.title')}
            </h2>
            {!loading && !loadError && (
                <span className="iab-src-count">
                    {t(visible.length === 1
                        ? 'ia_booster.sources.count_one' : 'ia_booster.sources.count_many',
                    {n: visible.length})}
                </span>
            )}
        </header>
    );

    if (loading) {
        return (
            <div className="iab-src">
                {head}
                <div className="box-loader"><Spinner color="positive"/></div>
            </div>
        );
    }

    return (
        <div className="iab-src">
            {head}
            <p className="iab-src-sub">
                {t('ia_booster.sources.sub', {client, interpolation: {escapeValue: false}})}
            </p>

            {loadError ? (
                // El tercer estado. Un error dibujado como dato ausente manda al usuario a cargar
                // otra vez algo que ya está — y acá el caso más común es un cliente al que esta
                // agencia no tiene acceso, donde «cargá material» es un consejo imposible.
                <div className="iab-src-failed" role="alert">
                    <WarningCircle size={20} aria-hidden="true"/>
                    <p>{t('ia_booster.sources.load_error')}</p>
                    <button type="button" className="iab-src-tool"
                            onClick={() => fetchRef.current(false)}>
                        <ArrowsClockwise size={13} aria-hidden="true"/>
                        {t('ia_booster.sources.load_retry')}
                    </button>
                </div>
            ) : (
                <>
                    {/* Las dos puertas para sumar material desde acá. Chicas y secundarias: la vía
                        principal sigue siendo la Barra Total del acto. Dice «Cargar documento» y no
                        «Cargar reunión» — no necesariamente es una reunión, y ese nombre dejaba
                        afuera al que viene con un brief en la mano. */}
                    <div className="iab-src-tools">
                        <button type="button" className="iab-src-tool" disabled={uploading}
                                onClick={() => fileRef.current && fileRef.current.click()}>
                            <UploadSimple size={13} aria-hidden="true"/>
                            {uploading
                                ? t('ia_booster.sources.uploading')
                                : t('ia_booster.sources.upload')}
                        </button>
                        <input ref={fileRef} type="file" multiple accept={INGEST_ACCEPT}
                               className="iab-src-file" onChange={pickFiles}
                               aria-label={t('ia_booster.sources.upload')}/>
                        {driveReady && (
                            <button type="button" className="iab-src-tool"
                                    onClick={() => setConnectOpen(true)}>
                                <Plus size={13} aria-hidden="true"/>
                                {t('client_memory.connections.connect_button')}
                            </button>
                        )}
                    </div>

                    {/* Un archivo rechazado deja rastro con su motivo y su reintento, como en la
                        Barra Total. Con cinco archivos y dos rechazos, dos toasts que se van no
                        alcanzan para saber cuáles fallaron. */}
                    {failedUploads.length > 0 && (
                        <ul className="iab-src-uploads">
                            {failedUploads.map((entry) => (
                                <li key={entry.id} className="iab-src-upload">
                                    <WarningCircle size={14} aria-hidden="true"/>
                                    <span className="iab-src-upload-name">{entry.name}</span>
                                    <span className="iab-src-upload-why">
                                        {entry.reasonKey
                                            ? t(entry.reasonKey, entry.reasonParams || {})
                                            : t('client_memory.toast.error')}
                                    </span>
                                    {/* `retryable` lo decide el hook: un formato que no entra o un
                                        kill-switch apagado dan el mismo resultado por más veces
                                        que se pruebe. Mismo criterio que la tira de la Barra
                                        Total. */}
                                    {entry.retryable && entry.retry && (
                                        <button type="button" className="iab-src-act"
                                                onClick={entry.retry}>
                                            {t('client_memory.documents.retry')}
                                        </button>
                                    )}
                                    <button type="button" className="iab-src-act"
                                            aria-label={t('ia_booster.sources.upload_dismiss')}
                                            onClick={() => dismissUpload && dismissUpload(entry.id)}>
                                        <X size={12} aria-hidden="true"/>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* El buscador va ANTES de los filtros de período y tipo: es la primera
                        pregunta de quien entra con un nombre en la cabeza («¿dónde está el correo
                        de Vaqueando?»), y los otros dos acotan. `type="search"` para que el
                        navegador ofrezca su propia X además de la nuestra. */}
                    <label className="iab-src-search">
                        <MagnifyingGlass size={13} weight="bold" aria-hidden="true"/>
                        <input type="search" value={query}
                               placeholder={t('ia_booster.sources.search_placeholder')}
                               aria-label={t('ia_booster.sources.search_label')}
                               onChange={(e) => setQuery(e.target.value)}/>
                        {query && (
                            <button type="button" className="iab-src-search__clear"
                                    aria-label={t('ia_booster.sources.search_clear')}
                                    onClick={() => setQuery('')}>
                                <X size={12} weight="bold" aria-hidden="true"/>
                            </button>
                        )}
                    </label>

                    <div className="iab-src-daterange">
                        <span className="iab-src-dk">{t('ia_booster.sources.period')}</span>
                        {[LIB_PERIOD.ALL, LIB_PERIOD.D30, LIB_PERIOD.D90].map((value) => (
                            <button key={value} type="button"
                                    className={`iab-src-preset ${preset === value ? '-on' : ''}`}
                                    aria-pressed={preset === value}
                                    onClick={() => applyPreset(value)}>
                                {t(`ia_booster.sources.period_${value}`)}
                            </button>
                        ))}
                        <span className="iab-src-dsep" aria-hidden="true"/>
                        <label className="iab-src-dfield">
                            {t('ia_booster.sources.from')}
                            <input type="date" value={range.from}
                                   onChange={(e) => setBound('from', e.target.value)}/>
                        </label>
                        <label className="iab-src-dfield">
                            {t('ia_booster.sources.to')}
                            <input type="date" value={range.to}
                                   onChange={(e) => setBound('to', e.target.value)}/>
                        </label>
                    </div>

                    <div className="iab-src-filters" role="group"
                         aria-label={t('ia_booster.sources.filter_kind')}>
                        <button type="button"
                                className={`iab-src-chip ${kind === LIB_ALL ? '-on' : ''}`}
                                aria-pressed={kind === LIB_ALL} onClick={() => setKind(LIB_ALL)}>
                            {t('ia_booster.sources.kind_all')}
                        </button>
                        {LIB_KIND_ORDER.map((value) => (
                            <button key={value} type="button"
                                    className={`iab-src-chip ${kind === value ? '-on' : ''}`}
                                    aria-pressed={kind === value} onClick={() => setKind(value)}>
                                {t(libKindLabelKey(value))}
                            </button>
                        ))}
                    </div>

                    {atCap && filtering && (
                        <p className="iab-src-cap">
                            {t('ia_booster.sources.cap_notice', {n: LIB_LIST_CAP})}
                        </p>
                    )}

                    {visible.length === 0 ? (
                        // Vacío discreto y con la razón correcta: «no hay nada cargado» y «no hay
                        // nada en este filtro» son dos situaciones distintas, y decir la primera
                        // cuando pasa la segunda manda a cargar de nuevo algo que ya está.
                        <div className="iab-src-empty">
                            <p>
                                {entries.length === 0
                                    ? t('ia_booster.sources.empty')
                                    : t('ia_booster.sources.empty_filtered')}
                            </p>
                            {/* Decir la razón correcta es la mitad; la otra es ofrecer la salida.
                                El resto de los vacíos del Booster la trae, y acá el filtro que
                                esconde el material lo puso el usuario hace tres teclas
                                (QA-VIS-ap-05). */}
                            {entries.length > 0 && (
                                <button type="button" className="iab-src-empty__clear"
                                        onClick={clearFilters}>
                                    {t('ia_booster.sources.clear_filters')}
                                </button>
                            )}
                        </div>
                    ) : (
                        <ul className="iab-src-list">
                            {visible.map((entry) => (
                                <SourceRow key={entry.key} entry={entry}
                                           busy={!!entry.document && busyId === entry.document.id}
                                           syncing={!!entry.connection
                                               && syncingId === entry.connection.id}
                                           canConnect={driveReady}
                                           audio={entry.document ? media[entry.document.id] : null}
                                           onViewRaw={setRawTarget}
                                           onRelabel={setKindTarget}
                                           onDelete={setDeleteTarget}
                                           onRetry={retry}
                                           onReprocess={setReprocessTarget}
                                           onRefreshUrl={refreshUrl}
                                           onListen={listen}
                                           onSyncConnection={() => syncConnection(entry.connection)}
                                           onReconnect={() => setConnectOpen(true)}/>
                            ))}
                        </ul>
                    )}
                </>
            )}

            <DocumentRawModal isOpen={!!rawTarget} document={rawTarget} clientId={clientId}
                              toggle={() => setRawTarget(null)}/>

            <DocumentKindModal isOpen={!!kindTarget} doc={kindTarget} isSaving={savingKind}
                               toggle={() => setKindTarget(null)} onConfirm={saveKind}/>

            {driveReady && (
                <DriveConnectModal isOpen={connectOpen} clientId={clientId} config={driveConfig}
                                   onClose={() => setConnectOpen(false)}
                                   onConnected={() => {
                                       setConnectOpen(false);
                                       fetchRef.current(true);
                                   }}/>
            )}

            <DocumentDeleteModal
                isOpen={!!deleteTarget}
                doc={deleteTarget}
                clientId={clientId}
                isSaving={deleting}
                toggle={() => setDeleteTarget(null)}
                onConfirm={remove}/>

            {/* El nombre del documento va DENTRO de la pregunta: la lista muestra varios «Volver a
                procesar» idénticos y la acción gasta crédito de IA (QA 2026-09-07, QA-VIS-cola-03). */}
            <YesNoModal
                skipRenderButton
                isOpen={!!reprocessTarget}
                title={t('client_memory.documents.reprocess_title')}
                question={reprocessTarget ? t(reprocessTarget.is_audio
                    ? 'client_memory.documents.reprocess_question_audio'
                    : 'client_memory.documents.reprocess_question',
                {title: reprocessTarget.title || t('client_memory.documents.untitled')}) : ''}
                onYesAnswer={reprocess}
                onNoAnswer={() => setReprocessTarget(null)}/>

            <YesNoModal
                skipRenderButton
                isOpen={!!confirmTarget}
                title={confirmTarget
                    ? t(REFRESH_CONFIRM_REASONS[confirmTarget.reason].title) : ''}
                question={confirmTarget
                    ? t(REFRESH_CONFIRM_REASONS[confirmTarget.reason].question, {
                        before: confirmTarget.body.chars_before,
                        after: confirmTarget.body.chars_after,
                        url: confirmTarget.body.final_url_after,
                        interpolation: {escapeValue: false},
                    }) : ''}
                onYesAnswer={() => refreshUrl(confirmTarget.doc, true)}
                onNoAnswer={() => setConfirmTarget(null)}/>
        </div>
    );
}

export default SourcesLibrary;
